import type { UserSummary } from '@/utils/models'
import * as axios from 'axios'
import React, { useState } from 'react'
import { toast } from 'sonner'
import { useGetCurrentUser } from '@/utils/api.ts'

export interface TAuthContext {
  isAuthenticated: boolean
  setToken: (token: string) => void
  isLoading: boolean // Add loading state
  logout: () => Promise<void>
  user: UserSummary | null
}

const AuthContext = React.createContext<TAuthContext | null>(null)

const TOKEN_KEY = 'eunice.auth.user'

function getStoredToken() {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    axios.default.defaults.headers.common.Authorization = `Bearer ${token}`
  }
  return token
}

function setStoredToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
    axios.default.defaults.headers.common.Authorization = `Bearer ${token}`
  }
  else {
    localStorage.removeItem(TOKEN_KEY)
    delete axios.default.defaults.headers.common.Authorization
  }
}

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [user, setUser] = useState<UserSummary | null>(null)
  const [token, setToken] = useState<string | null>(() => getStoredToken())
  const [isInitializing, setIsInitializing] = useState(true) // Add initializing state

  const {
    data: currentUserData,
    error: userError,
    isLoading: isUserLoading,
  } = useGetCurrentUser({
    query: {
      enabled: !!token, // Only run the query if the token exists
    },
  })

  // Update user when currentUserData changes
  React.useEffect(() => {
    if (currentUserData?.data) {
      setUser(currentUserData.data)
      setIsInitializing(false)
    }
    else if (!token) {
      // No token means we're not authenticated and not initializing
      setIsInitializing(false)
    }
  }, [currentUserData, token])

  const logout = React.useCallback(async () => {
    setStoredToken(null)
    setToken(null)
    setUser(null)

    // Redirect to login page after logout
    if (typeof window !== 'undefined') {
      // Check if we're in the former environment (subdirectory deployment)
      if (window.location.pathname.startsWith('/')) {
        window.location.href = '/login'
      }
      else {
        // Production environment (root deployment)
        window.location.href = '/login'
      }
    }
  }, [])

  const setTokenOnLogin = (token: string) => {
    setToken(token)
    setStoredToken(token)
  }

  // Initialize auth state on mount
  React.useEffect(() => {
    const initialToken = getStoredToken()
    setToken(initialToken)
    if (!initialToken) {
      // If no token, we're done initializing
      setIsInitializing(false)
    }
  }, [])

  // Handle error in fetching user
  React.useEffect(() => {
    if (userError && token) {
      // If we can't fetch the user with a token, the token might be invalid
      toast.error('Session expired. Please login again.')
      logout()
      setIsInitializing(false)
    }
  }, [userError, token, logout])

  const isAuthenticated = !!currentUserData?.data

  // isLoading is true if we're still initializing or waiting for user data
  const isLoading = isInitializing || (!!token && isUserLoading)

  const contextValue = React.useMemo(() => ({
    isAuthenticated,
    user,
    logout,
    setToken: setTokenOnLogin,
    isLoading,
  }), [isAuthenticated, isLoading, logout, user])

  return (
    <AuthContext value={contextValue}>
      {children}
    </AuthContext>
  )
}

export function useAuth() {
  const context = React.use(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
